import React, { useState, useCallback } from 'react';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { Lead, LeadSource, LeadStatus } from '../types';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { formatLocationForMaps } from '../utils/locationFormatter';
import { geocodeAddress } from '../utils/geocoding';
import { Inbox, CheckCircle, XCircle, FolderOpen, FileText, Rocket, Loader, ClipboardList } from 'lucide-react';

interface ParsedRow {
  [key: string]: string;
}

const ImportLeads: React.FC = () => {
  const { createLead } = useCRM();
  const { user } = useAuth();
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // CSV Parser
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCSV = (text: string): { headers: string[]; rows: ParsedRow[] } => {
    const lines = text.trim().split('\n');
    const headers = parseCSVLine(lines[0]);
    const rows: ParsedRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = parseCSVLine(lines[i]);
        const row: ParsedRow = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        rows.push(row);
      }
    }
    return { headers, rows };
  };

  // Convert row to lead data
  const rowToLead = (row: ParsedRow): Omit<Lead, 'id' | 'createdAt' | 'updatedAt'> & { createdAt?: Date; updatedAt?: Date } => {
    // Helper to find value from multiple possible column names
    const getColumnValue = (...possibleNames: string[]): string => {
      for (const name of possibleNames) {
        if (row[name] && row[name].trim()) {
          return row[name].trim();
        }
      }
      return '';
    };
    
    // Parse dates - handle format "2025-12-14 15:08:34"
    let createdAtDate: Date | undefined;
    let updatedAtDate: Date | undefined;
    
    // Check multiple possible column names for created date
    const createdAtValue = getColumnValue(
      'createdAt', 'created_at', 'Created', 'created', 'Created At', 'Created Date',
      'Lead - Lead created', 'Lead created', 'lead_created', 'createdat'
    );
    
    if (createdAtValue) {
      // Replace space with T for ISO format, handle timezone
      const dateStr = createdAtValue.replace(' ', 'T');
      createdAtDate = new Date(dateStr);
      if (isNaN(createdAtDate.getTime())) {
        // Try parsing as-is
        createdAtDate = new Date(createdAtValue);
      }
      if (isNaN(createdAtDate.getTime())) {
        createdAtDate = undefined;
      }
    }
    
    // Check multiple possible column names for updated date
    const updatedAtValue = getColumnValue(
      'updatedAt', 'updated_at', 'Updated', 'updated', 'Updated At', 'Updated Date',
      'Lead - Update time', 'Update time', 'update_time', 'updatedat', 'Modified', 'modified'
    );
    
    if (updatedAtValue) {
      const dateStr = updatedAtValue.replace(' ', 'T');
      updatedAtDate = new Date(dateStr);
      if (isNaN(updatedAtDate.getTime())) {
        updatedAtDate = new Date(updatedAtValue);
      }
      if (isNaN(updatedAtDate.getTime())) {
        updatedAtDate = undefined;
      }
    }
    
    // Parse labels
    let labels: string[] = [];
    if (row.labels) {
      labels = row.labels.split(',').map(l => l.trim()).filter(l => l);
    }
    
    // Validate source
    const validSources: LeadSource[] = ['web-form', 'facebook-form', 'instagram-form', 'ebook', 'instagram-follow', 'facebook-follow', 'b2b-visit', 'cold-call', 'call-in', 'referral', 'unspecified'];
    const source: LeadSource = validSources.includes(row.source as LeadSource) ? row.source as LeadSource : 'unspecified';
    
    // Validate status
    const validStatuses: LeadStatus[] = [
      'new',
      'contacted',
      'qualified',
      'lost',
      'nurture',
      'out-of-scope',
    ];
    const status: LeadStatus = validStatuses.includes(row.status as LeadStatus) ? row.status as LeadStatus : 'new';
    
    // Handle multiple possible phone column names and convert to string
    const phoneValue = row.phone || row.Phone || row['Phone Number'] || row['phone number'] || 
                       row.telephone || row.Telephone || row.cell || row.Cell || row.mobile || row.Mobile || '';
    const phoneStr = phoneValue ? String(phoneValue).trim() : '';
    
    return {
      firstName: row.firstName || row.FirstName || row['First Name'] || row['first name'] || 'Unknown',
      lastName: row.lastName || row.LastName || row['Last Name'] || row['last name'] || 'Unknown',
      email: row.email || row.Email || row.EMAIL || 'unknown@import.local',
      company: row.company || row.Company || row.COMPANY || '',
      phone: phoneStr,
      location: formatLocationForMaps(row.location || row.Location || row.address || row.Address) || '',
      source: source,
      metricsLiteId: row.metricsLiteId || '',
      projectType: row.projectType || 'Unspecified',
      budget: parseFloat(row.budget) || 0,
      timeline: row.timeline || 'TBD',
      status: status,
      labels: labels,
      notes: row.notes || '',
      archived: false,
      geocoded: false,
      createdAt: createdAtDate,
      updatedAt: updatedAtDate,
    };
  };

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      setStatus({ type: 'error', message: 'Please select a CSV file' });
      return;
    }
    
    setFileName(file.name);
    setStatus(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows } = parseCSV(text);
      setHeaders(headers);
      setParsedData(rows);
      setStatus({ type: 'info', message: `Found ${rows.length} leads ready to import` });
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  const startImport = async () => {
    if (!parsedData.length) return;
    
    setImporting(true);
    setProgress(0);
    setStatus({ type: 'info', message: 'Importing and geocoding...' });
    
    let imported = 0;
    let errors = 0;
    let geocoded = 0;
    
    // Determine collection based on test mode setting
    const useTestLeads = localStorage.getItem('crm_use_test_leads') === 'true';
    const collectionName = useTestLeads ? 'testLeads' : 'leads';
    
    for (let i = 0; i < parsedData.length; i++) {
      try {
        const leadData = rowToLead(parsedData[i]);
        const now = new Date();
        
        // Geocode the location if we have one
        let coordinates = leadData.coordinates;
        let isGeocoded = leadData.geocoded || false;
        
        if (leadData.location && !coordinates) {
          setStatus({ type: 'info', message: `Geocoding ${i + 1}/${parsedData.length}: ${leadData.location.substring(0, 30)}...` });
          try {
            const geocodedCoords = await geocodeAddress(leadData.location);
            if (geocodedCoords) {
              coordinates = geocodedCoords;
              isGeocoded = true;
              geocoded++;
            }
          } catch (geoErr) {
            console.warn('Geocoding failed for:', leadData.location, geoErr);
          }
        }
        
        // Write directly to Firestore to preserve original dates
        await addDoc(collection(db, collectionName), {
          ...leadData,
          coordinates: coordinates || { lat: 36.85, lng: -75.4 }, // Default: 50 miles off Norfolk
          geocoded: isGeocoded,
          // Use original dates if provided, otherwise use current time
          createdAt: leadData.createdAt ? Timestamp.fromDate(leadData.createdAt) : Timestamp.fromDate(now),
          updatedAt: leadData.updatedAt ? Timestamp.fromDate(leadData.updatedAt) : Timestamp.fromDate(now),
        });
        imported++;
      } catch (err) {
        console.error('Error importing row', i, err);
        errors++;
      }
      
      setProgress(Math.round(((i + 1) / parsedData.length) * 100));
    }
    
    setImporting(false);
    
    if (errors === 0) {
      setStatus({ type: 'success', message: `Imported ${imported} leads (${geocoded} geocoded)` });
      setParsedData([]);
      setHeaders([]);
      setFileName('');
    } else {
      setStatus({ type: 'error', message: `Imported ${imported} leads (${geocoded} geocoded), ${errors} failed` });
    }
  };

  return (
    <div style={{ 
      maxWidth: '900px', 
      margin: '0 auto', 
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <h1 style={{ color: 'var(--pm-accent)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}><Inbox size={28} /> Import Leads</h1>
      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '32px' }}>Upload a CSV file to import leads into your CRM</p>

      {/* Auth Status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        background: user ? '#d4edda' : '#f8d7da',
        borderRadius: '8px',
        marginBottom: '24px'
      }}>
        {user ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CheckCircle size={16} /> Logged in as <strong>{user.email}</strong></span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><XCircle size={16} /> Please log in to import leads</span>
        )}
      </div>

      {/* Drop Zone */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0 }}>1. Select CSV File</h3>
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => document.getElementById('fileInput')?.click()}
          style={{
            border: `2px dashed ${dragOver ? '#2C3E2F' : '#ccc'}`,
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? '#f8faf8' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          <p style={{ margin: 0, fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <FolderOpen size={20} /> Drag & drop your CSV file here, or click to select
          </p>
          <input
            type="file"
            id="fileInput"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
        {fileName && (
          <p style={{ marginTop: '12px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={16} /> {fileName}</p>
        )}
      </div>

      {/* Preview */}
      {parsedData.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0 }}>2. Preview Data ({parsedData.length} rows)</h3>
          <div style={{
            maxHeight: '300px',
            overflow: 'auto',
            fontSize: '12px',
            background: 'var(--color-surface)',
            borderRadius: '8px'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {headers.map((h, i) => (
                    <th key={i} style={{
                      padding: '8px',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      position: 'sticky',
                      top: 0
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, 10).map((row, i) => (
                  <tr key={i}>
                    {headers.map((h, j) => (
                      <td key={j} style={{
                        padding: '8px',
                        borderBottom: '1px solid var(--color-border)'
                      }}>{row[h] || ''}</td>
                    ))}
                  </tr>
                ))}
                {parsedData.length > 10 && (
                  <tr>
                    <td colSpan={headers.length} style={{
                      padding: '8px',
                      textAlign: 'center',
                      color: 'var(--color-text-muted)'
                    }}>... and {parsedData.length - 10} more rows</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import Button */}
      {parsedData.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0 }}>3. Import to CRM</h3>
          <p>Ready to import <strong>{parsedData.length}</strong> leads.</p>
          
          <button
            onClick={startImport}
            disabled={importing || !user}
            style={{
              background: importing ? '#ccc' : '#2C3E2F',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: importing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {importing ? <><Loader size={16} /> Importing...</> : <><Rocket size={16} /> Start Import</>}
          </button>

          {importing && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                height: '8px',
                background: 'var(--pm-surface-hover)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: '#2C3E2F',
                  transition: 'width 0.3s'
                }} />
              </div>
              <p style={{ marginTop: '8px', color: 'var(--color-text-secondary)' }}>{progress}% complete</p>
            </div>
          )}
        </div>
      )}

      {/* Status Message */}
      {status && (
        <div style={{
          padding: '16px',
          borderRadius: '8px',
          background: status.type === 'success' ? '#d4edda' : status.type === 'error' ? '#f8d7da' : '#d1ecf1',
          color: status.type === 'success' ? '#155724' : status.type === 'error' ? '#721c24' : '#0c5460'
        }}>
          {status.message}
        </div>
      )}

      {/* CSV Format Help */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginTop: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><ClipboardList size={20} /> Expected CSV Format</h3>
        <p style={{ fontFamily: 'monospace', fontSize: '12px', background: 'var(--color-surface)', padding: '12px', borderRadius: '4px', overflowX: 'auto' }}>
          firstName,lastName,email,company,location,source,metricsLiteId,projectType,budget,timeline,status,labels,createdAt,updatedAt
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          <strong>Valid sources:</strong> web-form, facebook-form, instagram-form, ebook, instagram-follow, facebook-follow, b2b-visit, cold-call, call-in, referral, unspecified
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          <strong>Valid statuses:</strong> new, contacted, qualified, lost, nurture
        </p>
      </div>
    </div>
  );
};

export default ImportLeads;
